// JOBE-0044.5: SPA document detail view at /library/:urn. Consumes the
// JOBE-0044.3 BFF endpoint (GET /api/documents/{urn}) and renders a
// read-mostly detail surface covering header, version timeline,
// provenance, collections, publications, and the post-ADR-0023 /
// post-JOBE-0089 containment relations (in_project, in_work_queue).
//
// Read-mostly per umbrella §R5. No edit affordances. Inline body
// rendering is out of scope — the per-version Open link to
// /api/artifacts/{urn} (JOBE-0092) is the binary entry point.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DocLink } from './components/DocLink.jsx';
import { useDocumentTitle } from './lib/useDocumentTitle.js';
import './library-detail.css';

// urnSlug returns the last `:`-separated segment of a URN, used as a
// best-effort display fallback when no `name` is available.
function urnSlug(u) {
  if (!u || typeof u !== 'string') return '';
  const idx = u.lastIndexOf(':');
  return idx >= 0 ? u.slice(idx + 1) : u;
}

// relTime renders an RFC3339 timestamp as a short relative string ("2h",
// "yesterday", "Apr 18"). Falls back to the raw input on parse failure
// so a malformed wire value still renders something rather than a blank.
function relTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d`;
  // Older than a week: short calendar form, locale-aware.
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// truncate clips long strings (typically commit messages) for display.
function truncate(s, n = 80) {
  if (!s || typeof s !== 'string') return '';
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

// copyURN copies the given URN to the clipboard. Defensive against the
// older non-async clipboard surface and against missing permissions.
function copyURN(urn) {
  if (!urn) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(urn).catch(() => {
      // Silently fail — there's no UI surface to report a clipboard
      // permission denial today; the user can copy via the address
      // bar or DevTools if it matters.
    });
  }
}

// useDocumentDetail polls once on mount. The detail surface doesn't
// need polling — the document's edges only change when a commit / move
// happens, and there's no UI affordance to mutate them from this page —
// so a single fetch keeps the page light and avoids gratuitous
// /api/documents/{urn} fan-out. Returns { data, error, loading } where
// `error` is { status, message } on failure.
function useDocumentDetail(urn) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // `tick` lets the retry button refetch without re-mounting.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!urn) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    fetch(`/api/documents/${encodeURIComponent(urn)}`, {
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) {
          setError({ status: r.status });
          setLoading(false);
          return;
        }
        const j = await r.json();
        if (!alive) return;
        setData(j);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive || err?.name === 'AbortError') return;
        setError({ status: 0, message: err?.message || String(err) });
        setLoading(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [urn, tick]);

  return {
    data,
    error,
    loading,
    retry: () => setTick((t) => t + 1),
  };
}

// LibraryDocumentDetail is the page entry point. Reads :urn from the
// route, fetches the detail payload, and renders the panel layout.
export function LibraryDocumentDetail() {
  const { urn: rawURN } = useParams();
  // useParams returns already-decoded values per react-router v7.
  const urn = rawURN || '';
  const { data, error, loading, retry } = useDocumentDetail(urn);

  // JOBE-0193: per-route document title — doc name once loaded,
  // generic 'Document' label while in-flight.
  useDocumentTitle(data?.name || data?.title || 'Document');

  return (
    <div className="library-detail-page">
      <Link to="/library" className="library-detail-back">← Library</Link>
      {loading && <DetailSkeleton />}
      {!loading && error && <DetailError error={error} onRetry={retry} />}
      {!loading && !error && data && <DetailBody doc={data} />}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <>
      <div className="library-detail-header library-detail-skeleton">
        <div className="skel-line skel-w-60" />
        <div className="skel-line skel-w-40" />
      </div>
      <div className="card library-detail-panel library-detail-skeleton">
        <h3>Versions</h3>
        <div className="skel-line skel-w-90" />
        <div className="skel-line skel-w-80" />
        <div className="skel-line skel-w-70" />
      </div>
    </>
  );
}

function DetailError({ error, onRetry }) {
  const status = error?.status || 0;
  if (status === 404) {
    return (
      <div className="card library-detail-empty">
        <h3>Document not found</h3>
        <p className="muted">
          No document with this URN is visible to you. It may have been
          removed, or the link may be stale.
        </p>
      </div>
    );
  }
  if (status === 403) {
    return (
      <div className="card library-detail-empty">
        <h3>Permission denied</h3>
        <p className="muted">
          You don't have permission to view this document.
        </p>
      </div>
    );
  }
  if (status === 400) {
    return (
      <div className="card library-detail-empty">
        <h3>Bad request</h3>
        <p className="muted">
          That doesn't look like a valid document URN.
        </p>
      </div>
    );
  }
  return (
    <div className="card library-detail-empty">
      <h3>Couldn't load this document</h3>
      <p className="muted">
        {status === 503
          ? 'The graph backend is unavailable right now.'
          : 'Something went wrong while loading the document.'}
      </p>
      <button type="button" className="library-detail-retry" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function DetailBody({ doc }) {
  // Versions arrive oldest-first from the wire (see openapi.yaml,
  // DocumentDetailVersion). The timeline reads naturally newest-first.
  const versionsDesc = Array.isArray(doc.versions)
    ? [...doc.versions].sort((a, b) => (b.n || 0) - (a.n || 0))
    : [];
  const hasDerived = Array.isArray(doc.derived_from) && doc.derived_from.length > 0;
  const hasCollections = Array.isArray(doc.collections) && doc.collections.length > 0;
  const hasPublications = Array.isArray(doc.publications) && doc.publications.length > 0;
  const hasContainment = doc.in_project || doc.in_work_queue;

  return (
    <>
      <DetailHeader doc={doc} />
      {hasContainment && <ContainmentRow doc={doc} />}
      <VersionTimeline versions={versionsDesc} />
      {hasDerived && <ProvenancePanel derivedFrom={doc.derived_from} />}
      {hasCollections && <CollectionsPanel collections={doc.collections} />}
      {hasPublications && <PublicationsPanel publications={doc.publications} />}
    </>
  );
}

function DetailHeader({ doc }) {
  const ownerLabel = urnSlug(doc.committed_by) || '—';
  return (
    <div className="library-detail-header">
      <div className="library-detail-title">
        <h2>{doc.name || urnSlug(doc.urn) || 'Untitled'}</h2>
        {doc.kind && <span className="badge library-detail-kind">{doc.kind}</span>}
      </div>
      <dl className="library-detail-meta">
        <div>
          <dt>Owner</dt>
          <dd title={doc.committed_by || ''}>{ownerLabel}</dd>
        </div>
        <div>
          <dt>Department</dt>
          <dd className="muted">—</dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd title={doc.created_at || ''}>{relTime(doc.created_at)}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd title={doc.updated_at || ''}>{relTime(doc.updated_at)}</dd>
        </div>
      </dl>
      {doc.description && (
        <p className="library-detail-description">{doc.description}</p>
      )}
    </div>
  );
}

function ContainmentRow({ doc }) {
  return (
    <div className="library-detail-containment">
      {doc.in_project && (
        <span className="library-detail-chip library-detail-chip-project">
          <span className="library-detail-chip-label">Part of project</span>
          <span className="library-detail-chip-name">
            {doc.in_project.name || urnSlug(doc.in_project.urn)}
          </span>
        </span>
      )}
      {doc.in_work_queue && (
        <span className="library-detail-chip library-detail-chip-queue">
          <span className="library-detail-chip-label">In queue</span>
          <span className="library-detail-chip-name">
            {doc.in_work_queue.name || urnSlug(doc.in_work_queue.urn)}
          </span>
        </span>
      )}
    </div>
  );
}

function VersionTimeline({ versions }) {
  return (
    <div className="card library-detail-panel">
      <h3>Versions</h3>
      {versions.length === 0 ? (
        <p className="muted">No committed versions yet.</p>
      ) : (
        <ul className="library-detail-version-list">
          {versions.map((v) => (
            <VersionRow key={v.urn || v.n} version={v} />
          ))}
        </ul>
      )}
    </div>
  );
}

function VersionRow({ version }) {
  const author = urnSlug(version.committed_by) || '—';
  const message = truncate(version.message || '', 80);
  const artifactURN = version.artifact_urn;
  return (
    <li className="library-detail-version-row">
      <div className="library-detail-version-head">
        <span className="library-detail-version-n">v{version.n}</span>
        <span
          className="library-detail-version-when"
          title={version.committed_at || ''}
        >
          {relTime(version.committed_at)}
        </span>
        <span className="library-detail-version-author" title={version.committed_by || ''}>
          {author}
        </span>
        {version.procedure_urn && (
          <span className="badge library-detail-version-procedure" title={version.procedure_urn}>
            From procedure
          </span>
        )}
      </div>
      {message && (
        <div className="library-detail-version-message" title={version.message}>
          {message}
        </div>
      )}
      <div className="library-detail-version-actions">
        {artifactURN ? (
          <a
            className="library-detail-version-open"
            href={`/api/artifacts/${encodeURIComponent(artifactURN)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </a>
        ) : (
          <span className="muted">No artifact</span>
        )}
        <button
          type="button"
          className="library-detail-copy"
          onClick={() => copyURN(version.urn)}
          title={`Copy ${version.urn || 'version URN'}`}
          aria-label="Copy version URN"
        >
          Copy URN
        </button>
      </div>
    </li>
  );
}

function ProvenancePanel({ derivedFrom }) {
  // Group by type so the panel reads "Derived from messages: …;
  // Derived from documents: …" rather than a flat list.
  const groups = new Map();
  derivedFrom.forEach((row) => {
    const t = row.type || 'other';
    if (!groups.has(t)) groups.set(t, []);
    groups.get(t).push(row);
  });
  return (
    <div className="card library-detail-panel">
      <h3>Provenance</h3>
      <div className="library-detail-provenance">
        {Array.from(groups.entries()).map(([type, items]) => (
          <div key={type} className="library-detail-provenance-group">
            <div className="library-detail-provenance-label">
              Derived from {type}{items.length === 1 ? '' : 's'}:
            </div>
            <div className="library-detail-chip-row">
              {items.map((item) => (
                <span key={item.urn} className="library-detail-chip">
                  <DocLink urn={item.urn} label={item.name || urnSlug(item.urn)} />
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CollectionsPanel({ collections }) {
  return (
    <div className="card library-detail-panel">
      <h3>Collections</h3>
      <div className="library-detail-chip-row">
        {collections.map((c) => (
          <Link
            key={c.urn}
            to={`/library?collection=${encodeURIComponent(c.urn)}`}
            className="library-detail-chip library-detail-chip-link"
          >
            {c.name || urnSlug(c.urn)}
          </Link>
        ))}
      </div>
    </div>
  );
}

function PublicationsPanel({ publications }) {
  return (
    <div className="card library-detail-panel">
      <h3>Publications</h3>
      <ul className="library-detail-publication-list">
        {publications.map((p) => (
          <li key={p.urn} className="library-detail-publication-row">
            <span className="library-detail-publication-title">
              {p.title || urnSlug(p.urn)}
            </span>
            <span className="library-detail-publication-when" title={p.published_at || ''}>
              {relTime(p.published_at)}
            </span>
            {p.audience && (
              <span className="library-detail-publication-audience" title={p.audience}>
                {urnSlug(p.audience)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
